// Підготовка фото перед завантаженням (у браузері):
// - будь-яке зображення, яке браузер вміє відкрити -> JPEG (iPhone HEIC у Safari теж)
// - враховує поворот камери (EXIF orientation)
// - зменшує до maxSide px і стискає, щоб файл вклався в ліміт сервера
// - EXIF (GPS, модель телефона тощо) НЕ потрапляє в новий файл
// - рахує SHA-256 готового файлу (відбиток, щоб довести незмінність)

const DEFAULT_MAX_SIDE = 2560;
const DEFAULT_MAX_BYTES = 4 * 1024 * 1024; // запит до Lambda обмежений ~6 МБ, з запасом

async function decode(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch { /* спробуємо через <img> */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unsupported')); };
    img.src = url;
  });
}

const toBlob = (canvas, quality) =>
  new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Image encoding failed'))), 'image/jpeg', quality));

export async function sha256Hex(blob) {
  const buf = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// file: File або Blob. Повертає { file, sha256, width, height, originalName, originalSize }
export async function prepareImage(file, { maxSide = DEFAULT_MAX_SIDE, maxBytes = DEFAULT_MAX_BYTES, baseName } = {}) {
  if (!file) throw new Error('No file selected');
  if (file.type && !file.type.startsWith('image/')) {
    throw new Error('Please choose an image file (JPEG, PNG, WebP or iPhone photo).');
  }

  let bitmap;
  try {
    bitmap = await decode(file);
  } catch {
    throw new Error('This image format cannot be opened in this browser. Please choose a JPEG or PNG photo.');
  }

  const srcW = bitmap.width, srcH = bitmap.height;
  if (!srcW || !srcH) throw new Error('The image is empty or damaged.');

  let scale = Math.min(1, maxSide / Math.max(srcW, srcH));
  let blob, w, h;

  // зменшуємо якість, а потім розмір, доки не вкладемося в ліміт
  for (let attempt = 0; attempt < 6; attempt++) {
    w = Math.max(1, Math.round(srcW * scale));
    h = Math.max(1, Math.round(srcH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; // прозорість PNG -> білий фон
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);

    for (const q of [0.92, 0.85, 0.75]) {
      blob = await toBlob(canvas, q);
      if (blob.size <= maxBytes) break;
    }
    if (blob.size <= maxBytes) break;
    scale *= 0.8;
  }
  if (bitmap.close) bitmap.close();
  if (blob.size > maxBytes) throw new Error('The image is too large. Please choose a smaller photo.');

  const original = (file.name || '').replace(/\.[^.]+$/, '');
  const name = `${(baseName || original || `photo-${Date.now()}`).replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80)}.jpg`;
  const out = new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });

  return {
    file: out,
    sha256: await sha256Hex(out),
    width: w,
    height: h,
    originalName: file.name || name,
    originalSize: file.size || null,
  };
}
